import { chipClass, cx } from "@studyward/ui";
import { WifiOff } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

// Form, feedback and loading pieces are shared with the admin console.
export { Badge, ErrorMessage, Field, FullPageLoading, Notice, Skeleton, inputClass } from "@studyward/ui";

/** Owl + letter-spaced STUDYWARD (web theme). A logotype: one image to assistive tech. */
export function Wordmark({ className, size, compact = false }: { className?: string; size?: number; compact?: boolean }) {
  const px = size ?? (compact ? 30 : 36);
  return (
    <span role="img" aria-label="StudyWard" className={cx("inline-flex items-center gap-2 text-text-primary", className)}>
      <img src="/owl-head.webp" alt="" width={px} height={px} />
      <span aria-hidden="true" className={cx("font-body font-bold uppercase", compact ? "text-small tracking-wider" : "text-h2 tracking-widest")}>
        Study<span className="text-text-link">Ward</span>
      </span>
    </span>
  );
}

/** One radio-style chip in a pill picker (programme, exam, daily goal): selected = dark fill, like the reference. */
export function ChoiceChip({ selected, onClick, children, label }: { selected: boolean; onClick: () => void; children: ReactNode; label?: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      onClick={onClick}
      className={chipClass(selected, "min-w-11")}
    >
      {children}
    </button>
  );
}

export const DISCLAIMER = "StudyWard is a study aid, not medical advice.";

// The AI data-use note (spec §11.3) comes from aiDataNote() in @studyward/shared: it depends on whether Pro runs on
// paid AI tiers yet (v1.7).

/** Friendly empty state: the owl, a plain-English line and one next step (first run, nothing due, nothing yet). */
export function OwlEmpty({ title, children, action, className }: { title: string; children?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cx("flex flex-col items-center gap-3 rounded-card bg-surface-card px-5 py-10 text-center shadow-card", className)}>
      <img src="/owl.webp" alt="" width={96} height={133} loading="lazy" className="h-28 w-auto" />
      <h2 className="max-w-md text-h2 font-medium">{title}</h2>
      {children && <p className="max-w-md text-small text-text-secondary">{children}</p>}
      {action}
    </div>
  );
}

/** True while the browser has a network connection. */
export function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

/** Shown across the app while offline: the website needs a connection to load sets and ask Hoot. */
export function OfflineNotice() {
  const online = useOnline();
  if (online) return null;
  return (
    <div role="status" className="mb-4 flex items-start gap-3 rounded-card bg-warning-soft px-5 py-4 text-small font-semibold text-warning-text">
      <WifiOff size={18} aria-hidden="true" className="mt-0.5 shrink-0" />
      <p className="min-w-0 flex-1">You're offline. Reconnect to load your sets, study and ask Hoot. Anything you've already answered is saved.</p>
    </div>
  );
}
